import { User } from 'types';
import { CLIENT_ID, CLIENT_SECRET } from 'constants/api';
import { api } from './api';
import { PricingPlan } from 'types';
import { verify } from 'crypto';
import { send } from 'process';

type RegisterCompanyPayload = {
  username: string;
  email: string;
  password: string;
  realm_name: string;
  realm_email?: string;
}; 

type RegisterCompanyResponse = {
  user: User;
  subscription: {
    id: number;
    pricing_plan: PricingPlan;
    realm: number;
    start_date: string;
    end_date: string | null;
    is_active: boolean;
  };
};



// Define a service using a base URL and expected endpoints
export const usersApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: ({ username, password }) => ({
        url: `/auth/token`,
        method: 'POST',
        body: { username, password, client_id: CLIENT_ID, client_secret: CLIENT_SECRET, grant_type: "password" },
      }),
    }),
    loginJWT: builder.mutation({
      query: ({ username, password }) => ({
        url: `/auth/login/`,
        method: 'POST',
        body: { username, password },
      }),
    }),
    googleLogin: builder.mutation({
      query: ({ token }) => ({
        url: `/auth/convert-token`,
        method: 'POST',
        body: { token, client_id: CLIENT_ID, client_secret: CLIENT_SECRET, grant_type: "convert_token", backend: "google-oauth2" },
      }),
    }),
    register: builder.mutation({
      query: ({ username, password }) => ({
        url: `/auth/token`,
        method: 'POST',
        body: { username, password, client_id: CLIENT_ID, client_secret: CLIENT_SECRET, grant_type: "password" },
      }),
    }),
    registerCompany: builder.mutation<RegisterCompanyResponse, RegisterCompanyPayload>({
      query: ({ username, email, password, realm_name, realm_email }) => ({
        url: `/register_company/`,
        method: 'POST',
        body: {
          username, email, password,
          realm: { name: realm_name, email: realm_email }
        },
      }),
    }),
    logout: builder.mutation({
        query: () => ({
          url: `/auth/logout/`,
          method: 'POST',
          body: { refresh: localStorage.getItem('refreshJWT') },
        }),
      }),
    refreshToken: builder.mutation({
        query: ({ refreshToken }) => ({
          url: `/auth/token`,
          method: 'POST',
          body: {
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            grant_type: "refresh_token",
            refresh_token: refreshToken,
          },
        }),
      }),
    refreshJWT: builder.mutation({
        query: ({ refresh }) => ({
          url: `/auth/token/`,
          method: 'POST',
          body: {
            refresh_token: refresh,
          },
        }),
      }),
    selectRealm: builder.mutation({
      query: ({ realm_id }) => ({
        url: `/auth/select-realm/`,
        method: 'POST',
        body: { realm_id },
      }),
    }),
    getUserRealms: builder.query({
      query: () => `/auth/realms/`,
      providesTags: [{ type: 'Users', id: 'USER_REALMS' }],
    }),
    getUserData: builder.query<User, void>({
      query: () => `/users/iam/`,
      providesTags: [{ type: 'Users', id: 'CURRENT_USER' }],
    }),
    requestVerificationEmail: builder.mutation<void, void>({
      query: () => ({
        url: `/auth/request-realm-verification/`,
        method: 'POST',
      }),
    }),
    sendVerificationCode: builder.mutation<void, { code: string }>({
      query: ({ code }) => ({
        url: `/auth/verify-realm/`,
        method: 'POST',
        body: { code },
      }),
    }),
  }),
})

export const { useLoginMutation, useRegisterMutation, useGoogleLoginMutation, useLogoutMutation,
               useRefreshTokenMutation, useGetUserDataQuery, useLoginJWTMutation, useRefreshJWTMutation,
               useSelectRealmMutation, useGetUserRealmsQuery, useRegisterCompanyMutation,
               useRequestVerificationEmailMutation, useSendVerificationCodeMutation, } = usersApiSlice